import type { AdminWhatsappInstanceRow, WhatsappStatusBucket } from '../../../domain/superadmin/types';
import {
    whatsappProviderLabel,
    whatsappStatusLabel,
} from '../../../domain/superadmin/whatsappFilters';
import { formatDate } from '../../../utils/formatters';
import { cn } from '@/lib/utils';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../../components/ui/table';
import { SectionEmpty } from '../organizations/sections/SectionEmpty';

interface WhatsappInstancesTableProps {
    items: AdminWhatsappInstanceRow[];
}

const STATUS_STYLES: Record<WhatsappStatusBucket, string> = {
    connected: 'border-success/30 bg-success-bg text-success-text',
    disconnected: 'border-error/40 bg-danger-bg text-danger-text',
    pending: 'border-warning/30 bg-warning-bg text-warning-text',
};

function StatusBadge({ bucket }: { bucket: WhatsappStatusBucket }) {
    return (
        <span
            className={cn(
                'inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium',
                STATUS_STYLES[bucket],
            )}
        >
            {whatsappStatusLabel(bucket)}
        </span>
    );
}

function InstanceCard({ row }: { row: AdminWhatsappInstanceRow }) {
    return (
        <article className="rounded-xl border border-subtle bg-card p-s4 shadow-sm">
            <header className="mb-s3 flex items-start justify-between gap-s2">
                <div className="min-w-0">
                    <h3 className="truncate text-sm font-semibold text-default">
                        {row.organizationName}
                    </h3>
                    <p className="truncate font-mono text-[13px] text-muted">
                        {row.instanceName || '—'}
                    </p>
                </div>
                <StatusBadge bucket={row.statusBucket} />
            </header>
            <dl className="grid grid-cols-2 gap-s2 text-[13px]">
                <div>
                    <dt className="text-[11px] font-semibold uppercase tracking-[0.07em] text-muted">
                        Telefone
                    </dt>
                    <dd className="font-mono tabular-nums text-default">
                        {row.phoneNumber || '—'}
                    </dd>
                </div>
                <div>
                    <dt className="text-[11px] font-semibold uppercase tracking-[0.07em] text-muted">
                        Provedor
                    </dt>
                    <dd className="text-secondary">{whatsappProviderLabel(row.provider)}</dd>
                </div>
                <div className="col-span-2">
                    <dt className="text-[11px] font-semibold uppercase tracking-[0.07em] text-muted">
                        Criada em
                    </dt>
                    <dd className="font-mono text-muted tabular-nums">
                        {row.createdAt ? formatDate(row.createdAt) : '—'}
                    </dd>
                </div>
            </dl>
        </article>
    );
}

export function WhatsappInstancesTable({ items }: WhatsappInstancesTableProps) {
    if (items.length === 0) {
        return <SectionEmpty message="Nenhuma conexão WhatsApp encontrada." />;
    }

    return (
        <section aria-label="Conexões WhatsApp">
            <ul className="grid list-none gap-s3 p-0 md:hidden">
                {items.map((row) => (
                    <li key={row.id}>
                        <InstanceCard row={row} />
                    </li>
                ))}
            </ul>

            <div className="hidden overflow-x-auto md:block">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Empresas</TableHead>
                            <TableHead>Instância</TableHead>
                            <TableHead>Telefone</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Provedor</TableHead>
                            <TableHead>Criada em</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {items.map((row) => (
                            <TableRow key={row.id}>
                                <TableCell>
                                    <span className="font-medium text-default">
                                        {row.organizationName}
                                    </span>
                                </TableCell>
                                <TableCell className="font-mono text-[13px] text-muted">
                                    {row.instanceName || '—'}
                                </TableCell>
                                <TableCell className="font-mono text-[13px] tabular-nums text-default">
                                    {row.phoneNumber || '—'}
                                </TableCell>
                                <TableCell>
                                    <StatusBadge bucket={row.statusBucket} />
                                </TableCell>
                                <TableCell className="text-secondary">
                                    {whatsappProviderLabel(row.provider)}
                                </TableCell>
                                <TableCell className="font-mono text-[13px] tabular-nums text-muted">
                                    {row.createdAt ? formatDate(row.createdAt) : '—'}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        </section>
    );
}
